import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { AlertTriangle, ArrowLeft, Edit3, FileUp, RefreshCw, Search, Send, UserPlus, X } from 'lucide-react';
import api from '../../../utils/authUtils';
import { INTEREST_CATEGORIES } from '../../../constants/doctorProfile';
import { statusMeta, SPEAKER_STATUS_KEYS, hasPendingTitleChange, hasPendingSlidesUpload, hasNotAcceptedInvite, hasNoSlidesUploaded, canInvite } from './speakerStatus';
import InviteModal from './InviteModal';
import {
  Container, BackLink, Header, Title, Subtitle, Card, Actions, Button, Message,
  List, ListItem, Badge, RowEvent, FilterChip, ChipClear, Empty, Select, Input,
  LinkRow, StatBar, StatPill, StatPillValue, StatPillLabel, WORKSPACE, eventLabel,
} from './ui';

const inviteRequestFrom = profile => ({
  fullName: profile.fullName, email: profile.email, title: profile.title, specialty: profile.specialty,
  institution: profile.institution, city: profile.city, country: profile.country,
  shortBioDe: profile.shortBioDe, shortBioEn: profile.shortBioEn, photoUrl: profile.photoUrl,
  linkedInUrl: profile.linkedInUrl, facebookUrl: profile.facebookUrl, instagramUrl: profile.instagramUrl,
  websiteUrl: profile.websiteUrl, professionalStatuses: profile.professionalStatuses, interests: profile.interests,
});

// Search/browse/quick-invite. Deep editing of a profile's researched fields
// happens on the detail page (Edit); this view is for finding a speaker and
// either re-inviting a known one or jumping to a new AI research.
const SpeakerList = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [profiles, setProfiles] = useState([]);
  const [events, setEvents] = useState([]);
  const [message, setMessage] = useState(null);
  const [inviteLink, setInviteLink] = useState('');
  const [inviting, setInviting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [startingResearchId, setStartingResearchId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const statusFilter = searchParams.get('status') || '';
  const titleChangesFilter = searchParams.get('titleChanges') === '1';
  const slidesFilter = searchParams.get('slidesChanges') === '1';
  const notAcceptedFilter = searchParams.get('notAccepted') === '1';
  const noSlidesFilter = searchParams.get('noSlides') === '1';
  const eventFilter = searchParams.get('event') || '';
  const topicFilter = searchParams.get('topic') || '';
  const interestLabel = interest => t(`speaker.interest.${interest}`, interest);
  const setFilter = (key, value) => { const next = new URLSearchParams(searchParams); if (value) next.set(key, value); else next.delete(key); setSearchParams(next, { replace: true }); };
  // The status/titleChanges/slidesChanges/notAccepted/noSlides filters are mutually
  // exclusive (each toggle clears the others) so the list always reads as a single
  // clear question; setExclusiveFilter centralizes that instead of repeating it per toggle.
  const setExclusiveFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    ['status', 'titleChanges', 'slidesChanges', 'notAccepted', 'noSlides'].forEach(k => next.delete(k));
    if (value) next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const clearStatusFilters = () => setExclusiveFilter();
  const toggleStatus = key => setExclusiveFilter('status', statusFilter === key ? null : key);
  const toggleTitleChanges = () => setExclusiveFilter('titleChanges', titleChangesFilter ? null : '1');
  const toggleSlidesChanges = () => setExclusiveFilter('slidesChanges', slidesFilter ? null : '1');
  const toggleNotAccepted = () => setExclusiveFilter('notAccepted', notAcceptedFilter ? null : '1');
  const toggleNoSlides = () => setExclusiveFilter('noSlides', noSlidesFilter ? null : '1');

  const load = async () => {
    try {
      const { data } = await api.get('/api/cms/speaker-profiles');
      setProfiles(data || []);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else setMessage({ error: true, text: t('speakerMgmt.msg.loadFailed', 'Could not load speaker drafts.') });
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/api/cms/event/options').then(({ data }) => setEvents(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!profiles.some(p => ['PENDING', 'RUNNING'].includes(p.enrichmentStatus))) return undefined;
    const timer = setInterval(load, 4000); return () => clearInterval(timer);
  }, [profiles]); // eslint-disable-line react-hooks/exhaustive-deps

  const eventFilterLabel = events.find(e => String(e.id) === eventFilter);
  const statusCounts = SPEAKER_STATUS_KEYS.map(key => ({
    key,
    count: profiles.filter(p => statusMeta(p).key === key).length,
    meta: profiles.map(statusMeta).find(m => m.key === key) || { bg: '#f1f5f9', fg: '#475569', id: `speakerMgmt.status.${key}` },
  })).filter(c => c.count > 0);
  const titleChangeCount = profiles.filter(hasPendingTitleChange).length;
  const slidesChangeCount = profiles.filter(hasPendingSlidesUpload).length;
  const notAcceptedCount = profiles.filter(hasNotAcceptedInvite).length;
  const noSlidesCount = profiles.filter(hasNoSlidesUploaded).length;
  const shown = profiles.filter(p => {
    if (titleChangesFilter && !hasPendingTitleChange(p)) return false;
    if (slidesFilter && !hasPendingSlidesUpload(p)) return false;
    if (notAcceptedFilter && !hasNotAcceptedInvite(p)) return false;
    if (noSlidesFilter && !hasNoSlidesUploaded(p)) return false;
    if (statusFilter && statusMeta(p).key !== statusFilter) return false;
    if (eventFilter && !(p.engagements || []).some(e => String(e.eventId) === eventFilter)) return false;
    if (topicFilter && !(p.interests || []).includes(topicFilter)) return false;
    if (query.trim()) {
      const needle = query.trim().toLowerCase();
      if (!p.fullName?.toLowerCase().includes(needle) && !p.email?.toLowerCase().includes(needle)) return false;
    }
    return true;
  });

  const startInvite = profile => { setMessage(null); setInviteLink(''); setInviting(profile); };
  // Starts AI research for a profile that's never had any (bulk-imported
  // drafts land here unqueued by design - see SpeakerDetectionService).
  // Same endpoint the detail page's "Retry research" button uses; reusable
  // here because a never-queued profile has no job to retry, just to start.
  const startResearch = async profile => {
    setStartingResearchId(profile.id); setMessage(null);
    try {
      await api.post(`/api/cms/speaker-profiles/${profile.id}/retry`);
      await load();
    } catch (err) { setMessage({ error: true, text: err.response?.data || t('speakerMgmt.msg.retryFailed', 'Could not retry research.') }); }
    finally { setStartingResearchId(null); }
  };
  const confirmInvite = async (eventId, talkTitle, requiresPresentation) => {
    setBusy(true); setMessage(null); setInviteLink('');
    try {
      const { data } = await api.post(
        `/api/cms/speaker-profiles/${inviting.id}/approve-and-invite?eventId=${eventId}&talkTitle=${encodeURIComponent(talkTitle)}&requiresPresentation=${requiresPresentation}`,
        { profile: inviteRequestFrom(inviting), resolutions: {} }
      );
      if (data.emailSent) setMessage({ text: intl.formatMessage({ id: 'speakerMgmt.invite.sent' }, { email: inviting.email }) });
      else {
        setInviteLink(`${window.location.origin}/speaker/invite?token=${data.inviteToken}`);
        setMessage({ error: true, text: intl.formatMessage({ id: 'speakerMgmt.invite.emailFailed' }) });
      }
      setInviting(null);
      await load();
    } catch (err) { setMessage({ error: true, text: err.response?.data || intl.formatMessage({ id: 'speakerMgmt.invite.error' }) }); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <BackLink to="/admin/dashboard"><ArrowLeft size={16} /><FormattedMessage id="speakerMgmt.back" defaultMessage="Back to Dashboard" /></BackLink>
      <Header>
        <Title><FormattedMessage id="speakerMgmt.list.title" defaultMessage="Speakers" /></Title>
        <Actions style={{ margin: 0 }}>
          <Button onClick={() => navigate(`${WORKSPACE}/new`)}><UserPlus size={16} /><FormattedMessage id="speakerMgmt.overview.newSpeaker" defaultMessage="Research a new speaker" /></Button>
          <Button secondary onClick={() => navigate(`${WORKSPACE}/detect`)}><FileUp size={16} /><FormattedMessage id="speakerMgmt.overview.aiExtract" defaultMessage="AI-extract speakers from program" /></Button>
          <Button secondary onClick={load}><RefreshCw size={16} /><FormattedMessage id="speakerMgmt.refresh" defaultMessage="Refresh" /></Button>
        </Actions>
      </Header>
      <Subtitle><FormattedMessage id="speakerMgmt.list.subtitle" defaultMessage="Find a speaker to invite them to another event, or open their profile to edit it." /></Subtitle>
      {message && <Message error={message.error}>{message.text}</Message>}
      {inviteLink && <Card><LinkRow><code>{inviteLink}</code><Button onClick={() => navigator.clipboard.writeText(inviteLink)}><FormattedMessage id="speakerMgmt.copy" defaultMessage="Copy" /></Button></LinkRow></Card>}

      {titleChangeCount > 0 && (
        <Card style={{ borderLeft: '4px solid #92400e', background: '#fffbeb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#92400e" />
              <strong>
                <FormattedMessage id="speakerMgmt.titleChanges.banner"
                  defaultMessage="{count, plural, one {# speaker has} other {# speakers have}} a pending talk-title change request."
                  values={{ count: titleChangeCount }} />
              </strong>
            </div>
            <Button onClick={toggleTitleChanges}>
              {titleChangesFilter
                ? <FormattedMessage id="speakerMgmt.titleChanges.showAll" defaultMessage="Show all speakers" />
                : <FormattedMessage id="speakerMgmt.titleChanges.review" defaultMessage="Review requests" />}
            </Button>
          </div>
        </Card>
      )}

      {slidesChangeCount > 0 && (
        <Card style={{ borderLeft: '4px solid #075985', background: '#f0f9ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#075985" />
              <strong>
                <FormattedMessage id="speakerMgmt.slidesChanges.banner"
                  defaultMessage="{count, plural, one {# speaker has} other {# speakers have}} uploaded slides you haven't reviewed yet."
                  values={{ count: slidesChangeCount }} />
              </strong>
            </div>
            <Button onClick={toggleSlidesChanges}>
              {slidesFilter
                ? <FormattedMessage id="speakerMgmt.slidesChanges.showAll" defaultMessage="Show all speakers" />
                : <FormattedMessage id="speakerMgmt.slidesChanges.review" defaultMessage="Review uploads" />}
            </Button>
          </div>
        </Card>
      )}

      {notAcceptedCount > 0 && (
        <Card style={{ borderLeft: '4px solid #5b21b6', background: '#f5f3ff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#5b21b6" />
              <strong>
                <FormattedMessage id="speakerMgmt.notAccepted.banner"
                  defaultMessage="{count, plural, one {# speaker has} other {# speakers have}} not accepted their invite yet."
                  values={{ count: notAcceptedCount }} />
              </strong>
            </div>
            <Button onClick={toggleNotAccepted}>
              {notAcceptedFilter
                ? <FormattedMessage id="speakerMgmt.notAccepted.showAll" defaultMessage="Show all speakers" />
                : <FormattedMessage id="speakerMgmt.notAccepted.review" defaultMessage="Show them" />}
            </Button>
          </div>
        </Card>
      )}

      {noSlidesCount > 0 && (
        <Card style={{ borderLeft: '4px solid #b91c1c', background: '#fef2f2' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <AlertTriangle size={18} color="#b91c1c" />
              <strong>
                <FormattedMessage id="speakerMgmt.noSlides.banner"
                  defaultMessage="{count, plural, one {# speaker hasn't} other {# speakers haven't}} uploaded their slides yet."
                  values={{ count: noSlidesCount }} />
              </strong>
            </div>
            <Button onClick={toggleNoSlides}>
              {noSlidesFilter
                ? <FormattedMessage id="speakerMgmt.noSlides.showAll" defaultMessage="Show all speakers" />
                : <FormattedMessage id="speakerMgmt.noSlides.review" defaultMessage="Show them" />}
            </Button>
          </div>
        </Card>
      )}

      {profiles.length > 0 && (
        <StatBar>
          <StatPill type="button" $active={!statusFilter && !titleChangesFilter && !slidesFilter && !notAcceptedFilter && !noSlidesFilter} $accent="#111D4E" onClick={clearStatusFilters}>
            <StatPillValue $active={!statusFilter && !titleChangesFilter && !slidesFilter && !notAcceptedFilter && !noSlidesFilter}>{profiles.length}</StatPillValue>
            <StatPillLabel $active={!statusFilter && !titleChangesFilter && !slidesFilter && !notAcceptedFilter && !noSlidesFilter}><FormattedMessage id="speakerMgmt.overview.total" defaultMessage="All speakers" /></StatPillLabel>
          </StatPill>
          {statusCounts.map(c => (
            <StatPill key={c.key} type="button" $active={statusFilter === c.key} $accent={c.meta.fg} onClick={() => toggleStatus(c.key)}>
              <StatPillValue $active={statusFilter === c.key}>{c.count}</StatPillValue>
              <StatPillLabel $active={statusFilter === c.key}><FormattedMessage id={c.meta.id} defaultMessage={c.key} /></StatPillLabel>
            </StatPill>
          ))}
        </StatBar>
      )}

      <Card>
        <div style={{ position: 'relative', marginBottom: '.9rem' }}>
          <Search size={16} style={{ position: 'absolute', left: '.7rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <Input style={{ width: '100%', paddingLeft: '2.1rem' }} value={query} onChange={e => setFilter('q', e.target.value)}
            placeholder={t('speakerMgmt.list.searchPlaceholder', 'Search speakers by name or email…')} />
        </div>
        {eventFilter && <FilterChip><FormattedMessage id="speakerMgmt.filtered" defaultMessage="Filtered:" /> <FormattedMessage id="speakerMgmt.filter.event" defaultMessage="Event:" /> {eventFilterLabel ? eventFilterLabel.name : eventFilter}<ChipClear onClick={() => setFilter('event', '')} title={t('speakerMgmt.clearFilter', 'Clear')}><X size={13} /></ChipClear></FilterChip>}
        {topicFilter && <FilterChip><FormattedMessage id="speakerMgmt.filtered" defaultMessage="Filtered:" /> <FormattedMessage id="speakerMgmt.filter.topic" defaultMessage="Topic:" /> {interestLabel(topicFilter)}<ChipClear onClick={() => setFilter('topic', '')} title={t('speakerMgmt.clearFilter', 'Clear')}><X size={13} /></ChipClear></FilterChip>}
        <Select value={eventFilter} onChange={e => setFilter('event', e.target.value)} style={{ marginBottom: '.7rem', width: '100%' }} aria-label={t('speakerMgmt.filter.event', 'Event:')}>
          <option value="">{t('speakerMgmt.filter.eventAll', 'All events')}</option>
          {events.map(ev => <option key={ev.id} value={ev.id}>{eventLabel(ev, intl)}</option>)}
        </Select>
        <Select value={topicFilter} onChange={e => setFilter('topic', e.target.value)} style={{ marginBottom: '.7rem', width: '100%' }} aria-label={t('speakerMgmt.filter.topic', 'Topic:')}>
          <option value="">{t('speakerMgmt.filter.topicAll', 'All topics')}</option>
          {INTEREST_CATEGORIES.map(category => (
            <optgroup key={category.key} label={t(category.labelId, category.labelDefault)}>
              {category.interests.map(i => <option key={i} value={i}>{interestLabel(i)}</option>)}
            </optgroup>
          ))}
        </Select>
        {shown.length ? <List>{shown.map(profile => {
          const invitable = canInvite(profile);
          return (
            <ListItem key={profile.id}>
              <div style={{ flex: 1, minWidth: '12rem' }}>
                <Link to={`${WORKSPACE}/${profile.id}`} style={{ fontWeight: 700, color: '#111D4E', textDecoration: 'none' }}>{profile.fullName}</Link>
                <div>{profile.email || <span style={{ color: '#94a3b8' }}>{['PENDING', 'RUNNING'].includes(profile.enrichmentStatus)
                  ? <FormattedMessage id="speakerMgmt.emailPending" defaultMessage="Email pending research" />
                  : <FormattedMessage id="speakerMgmt.emailNotResearched" defaultMessage="No email yet" />}</span>}</div>
                {(() => { const m = statusMeta(profile); return <Badge style={{ background: m.bg, color: m.fg }}><FormattedMessage id={m.id} defaultMessage={m.def} /></Badge>; })()}
                {hasPendingTitleChange(profile) && <Badge style={{ background: '#fef3c7', color: '#92400e', marginLeft: '.35rem' }}><FormattedMessage id="speakerMgmt.talk.suggestionPending" defaultMessage="Title change requested" /></Badge>}
                {hasPendingSlidesUpload(profile) && <Badge style={{ background: '#e0f2fe', color: '#075985', marginLeft: '.35rem' }}><FormattedMessage id="speakerMgmt.talk.slidesPending" defaultMessage="New slides to review" /></Badge>}
                {(profile.engagements || []).length > 0 && <RowEvent>→ {(profile.engagements || []).map(e => e.eventName).join(' · ')}</RowEvent>}
              </div>
              <Actions style={{ margin: 0 }}>
                <Button secondary as={Link} to={`${WORKSPACE}/${profile.id}`}><Edit3 size={15} /><FormattedMessage id="speakerMgmt.list.edit" defaultMessage="View & edit" /></Button>
                {statusMeta(profile).key === 'notQueued' && (
                  <Button secondary disabled={startingResearchId === profile.id} onClick={() => startResearch(profile)}>
                    <Search size={15} />{startingResearchId === profile.id
                      ? <FormattedMessage id="speakerMgmt.list.startingResearch" defaultMessage="Starting…" />
                      : <FormattedMessage id="speakerMgmt.list.startResearch" defaultMessage="Start research" />}
                  </Button>
                )}
                <Button disabled={!invitable} title={invitable ? undefined : t('speakerMgmt.list.inviteBlocked', 'This profile still needs AI research before it can be invited.')} onClick={() => startInvite(profile)}>
                  <Send size={15} /><FormattedMessage id="speakerMgmt.list.invite" defaultMessage="Invite" />
                </Button>
              </Actions>
            </ListItem>
          );
        })}</List> : <Empty><FormattedMessage id="speakerMgmt.noDrafts" defaultMessage="No speaker drafts yet." /></Empty>}
      </Card>

      <InviteModal open={!!inviting} onClose={() => setInviting(null)} onConfirm={confirmInvite} busy={busy}
        events={events} email={inviting?.email} hasAccount={!!inviting?.accountId}
        prefillTalkTitle={inviting?.detectedTalkTitle} prefillRequiresPresentation={inviting?.detectedNeedsPresentation} />
    </Container>
  );
};

export default SpeakerList;
